import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/UserContext';
import authServices from '../../services/AuthServices';
// import GoogleSignIn from './GoogleSignIn';

export default function LoginForm() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { setCredentials, setUser, isAuth } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuth) {
            navigate('/auth/profile', { replace: true });
        }
    }, [isAuth, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError('Please fill in all fields.');
            return;
        }

        setLoading(true);

        try {
            const res = await authServices.login({ email, password });
            setCredentials({
                access: res.data.access,
                refresh: res.data.refresh,
            });
            const userRes = await authServices.getUserDetails();
            setUser(userRes.data);
            navigate('/auth/profile', { replace: true });
            setTimeout(() => {
                toast.success('Welcome back!');
            }, 400);
        } catch (err) {
            console.error('Login error:', err);
            let errorMessage = 'Invalid email or password';
            if (err.response?.data) {
                const data = err.response.data;
                if (data.detail) {
                    errorMessage = data.detail;
                } else if (Array.isArray(data.non_field_errors)) {
                    errorMessage = data.non_field_errors[0];
                } else if (typeof data === 'string') {
                    errorMessage = data;
                }
            }

            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // const handleGoogleToken = async (idToken) => {
    //     setLoading(true);
    //     try {
    //         const res = await authServices.googleAuth({
    //             auth_token: idToken,
    //         })

    //         console.log(res.data);
            

    //         const { access, refresh, user, is_new } = res.data;

    //         setCredentials({
    //             access: access,
    //             refresh: refresh,
    //         });

    //         setUser(user);
    //         navigate('/auth/profile', { replace: true });

    //         let msg = is_new ? 'Your account has been set up. Welcome!' : 'Welcome back!'

    //         setTimeout(() => {
    //             toast.success(msg);
    //         }, 400);

    //     } catch (err) {
    //         console.error('Register error:', err);

    //         let errorMessage = 'Failed to create account with Google';

    //         if (err.response?.status === 400 && Array.isArray(err.response.data?.details)) {
    //             const messages = err.response.data.details.map(d => {
    //                 const fieldName = d.field ? `${d.field.charAt(0).toUpperCase() + d.field.slice(1)}` : 'Field';
    //                 return `${fieldName}: ${d.message}`;
    //             });
    //             errorMessage = messages.join('; ') || 'Validation failed';
    //         } else if (err.response?.status === 400 && err.response.data) {
    //             const data = err.response.data;
    //             const fieldErrors = Object.entries(data)
    //                 .flatMap(([field, msgs]) =>
    //                     (Array.isArray(msgs) ? msgs : [msgs])
    //                         .filter(msg => typeof msg === 'string')
    //                         .map(msg => `${field.charAt(0).toUpperCase() + field.slice(1)}: ${msg}`)
    //                 );
    //             errorMessage = fieldErrors.join('; ') || 'Invalid input';
    //         } else if (err.request) {
    //             errorMessage = 'Network error — please check your connection';
    //         } else {
    //             errorMessage = err.message || 'Something went wrong';
    //         }

    //         setError(errorMessage);
    //         toast.error(errorMessage);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    return (
        <div className="max-w-md w-full">
            <div className="bg-white dark:bg-slate-900 p-8 border rounded-lg shadow-2xl">
                <div className="mb-8">
                    <h2 className="text-3xl font-bold text-primary dark:text-secondary">Sign In</h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                        Enter your credentials to continue.
                    </p>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                        {error}
                    </div>
                )}

                <form className="space-y-6" onSubmit={handleSubmit}>
                    <div>
                        <label
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            htmlFor="email"
                        >
                            Email: *
                        </label>
                        <div className="mt-1">
                            <input
                                autoComplete="email"
                                className="input input-bordered w-full border p-2 rounded-md border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                                id="email"
                                name="email"
                                required
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div>
                        <label
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            htmlFor="password"
                        >
                            Password: *
                        </label>
                        <div className="mt-1 relative">
                            <input
                                autoComplete="current-password"
                                className="input input-bordered w-full border p-2 rounded-md border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-gray-900 dark:text-white pr-10"
                                id="password"
                                name="password"
                                required
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex={-1}
                            >
                                {showPassword ? '.Hide' : 'Show'}
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-4 justify-between">
                        <label className="flex items-center">
                            <input
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-secondary"
                                type="checkbox"
                                checked={showPassword}
                                onChange={() => setShowPassword(!showPassword)}
                            />
                            <span className="ml-2 text-sm text-gray-900 dark:text-gray-300">
                                Show Password
                            </span>
                        </label>

                        <Link
                            className="text-sm font-medium text-secondary hover:text-orange-400"
                            to="/auth/forgot"
                        >
                            Forgot Password?
                        </Link>
                    </div>

                    <div>
                        <button
                            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? 'Signing in...' : 'Login'}
                        </button>
                    </div>
                </form>

                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Don’t have an account?{' '}
                        <Link
                            className="font-medium text-secondary hover:text-orange-400 ml-1"
                            to="/auth/register"
                        >
                            Sign up
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}